// "How did the call go?" after a call-back.
//
// Primary: the caller-types outcome endpoint (contract ~/pm/contracts/B4.md v1.2 §3,
// CU-869fm5hhm):
//   POST /api/v1/practices/{p}/calls/{id}/outcome/  { outcome, note }  + Idempotency-Key
// It counts attempts (3 → "couldn't reach") and flags the number for Not a patient.
//
// Fallback while that endpoint isn't deployed (404 on a row without next_actions):
// the existing follow-up endpoint
// (callFollowUp.api.js). Spoke closes the call-back, No answer / Left voicemail count
// an attempt, Not a patient stops the call-back for this call only.
import { callApi } from '../../../utils/apiCall';
import newIdempotencyKey from '../../../utils/idempotencyKey';
import { callFollowUpUrl } from './callFollowUp.api';

export const CALL_OUTCOME = {
  // Book appointment on the outcome screen (CU-869fmc59c). An older backend refuses the
  // choice with a 400; recordCallOutcome then records `spoke`, which closes the call-back the same way.
  booked: { key: 'booked', label: 'Booked', action: 'responded', note: 'Called back: booked an appointment' },
  spoke: { key: 'spoke', label: 'Spoke', action: 'responded', note: 'Called back: spoke to the caller' },
  no_answer: { key: 'no_answer', label: 'No answer', action: 'attempted', note: 'Called back: no answer' },
  left_voicemail: { key: 'left_voicemail', label: 'Left voicemail', action: 'attempted', note: 'Called back: left a voicemail' },
  not_a_patient: { key: 'not_a_patient', label: 'Not a patient', action: 'ignored', note: 'Called back: not a patient' },
};
export const CALL_OUTCOME_OPTIONS = Object.values(CALL_OUTCOME).filter((o) => o.key !== 'booked');

export const callOutcomeUndoUrl = (practiceId, callId) => `${`/api/v1/practices/${practiceId}/calls/${encodeURIComponent(callId)}/outcome/`}undo/`;

export const callOutcomeUrl = (practiceId, callId) => `/api/v1/practices/${practiceId}/calls/${encodeURIComponent(callId)}/outcome/`;

const PERMISSION_MESSAGE = "You don't have permission to record this outcome. Ask an admin.";

const refusal = (response) => {
  if (response.status === 403) return PERMISSION_MESSAGE;
  const data = response.data || {};
  const detail = data.detail || (Array.isArray(data) ? data[0] : null) || (Array.isArray(data.outcome) ? data.outcome[0] : null);
  return typeof detail === 'string' ? detail : 'The outcome could not be saved.';
};

// { ok: true } saved · { uncertain: true } no response or a 5xx: the write may have
// committed, so never say "failed" and never invite a retry · { ok: false, message }
// refused. Pass one `idempotencyKey` per user choice and reuse it on any retry of
// that choice, so the backend can drop a repeat.
export async function recordCallOutcome(practiceId, row, outcomeKey, { idempotencyKey = newIdempotencyKey() } = {}) {
  const outcome = CALL_OUTCOME[outcomeKey];
  if (!outcome || !row?.id) return { ok: false, message: 'This outcome cannot be recorded for this call.' };
  // The dialog owns error text (a 5xx is "unknown", not "failed"), and a slow save
  // must not be cut off at the default 10 s while the server still commits it.
  const options = { headers: { 'Idempotency-Key': idempotencyKey }, suppressServerErrorDispatch: true, timeout: 30000 };
  let response = await callApi(callOutcomeUrl(practiceId, row.id), 'POST', { outcome: outcome.key, note: outcome.note }, false, options);
  if (outcome.key === 'booked' && response?.status === 400) {
    const { spoke } = CALL_OUTCOME;
    response = await callApi(callOutcomeUrl(practiceId, row.id), 'POST', { outcome: spoke.key, note: spoke.note }, false, { ...options, headers: { 'Idempotency-Key': newIdempotencyKey() } });
  }
  // Fall back only for rows from a backend without next_actions (outcome endpoint not
  // deployed). Once it is live a 404 means the call itself is gone or hidden.
  if (response?.status === 404 && !Array.isArray(row.nextActions)) {
    response = await callApi(callFollowUpUrl(practiceId, row.id), 'POST', { action: outcome.action, via: 'call', note: outcome.note }, false, options);
  }
  // `data` = the updated row (B4 §3), so the caller can say which try it was.
  if (response?.ok) return { ok: true, data: response.data };
  if (!response || response.status === 0 || response.status >= 500) return { ok: false, uncertain: true };
  return { ok: false, status: response.status, message: refusal(response) };
}

// Undo a manual No answer attempt: `POST calls/{id}/outcome/undo/` with the SAME Idempotency-Key the
// no_answer POST used. { ok: true, data: row } · { unsupported: true } older backend (404) ·
// { ok: false, message } too late / nothing to undo / refused · { uncertain: true } no reply or 5xx.
export async function undoCallAttempt(practiceId, row, { idempotencyKey } = {}) {
  if (!row?.id) return { ok: false, message: 'This outcome cannot be undone.' };
  const options = { headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}, suppressServerErrorDispatch: true, timeout: 30000 };
  const response = await callApi(callOutcomeUndoUrl(practiceId, row.id), 'POST', {}, false, options);
  if (response?.ok) return { ok: true, data: response.data };
  if (!response || response.status === 0 || response.status >= 500) return { ok: false, uncertain: true };
  if (response.status === 404) return { ok: false, unsupported: true, status: 404 };
  const code = response.data?.code;
  if (response.status === 409 && (code === 'undo_expired' || code === 'nothing_to_undo')) {
    return { ok: false, status: 409, message: code === 'undo_expired' ? 'Too late to undo.' : 'Nothing to undo: it was already changed.' };
  }
  return { ok: false, status: response.status, message: refusal(response) };
}
